import { ModalContainer, CtaButton } from '@tassomutuo-backoffice/ui';
import { toast } from 'react-toastify';
import {
  Autocomplete,
  Box,
  CircularProgress,
  debounce,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { TeamGroup, User } from '@tassomutuo-backoffice/types';
import { useCallback, useState, useEffect } from 'react';
import { getUsers } from '../../../../services/api/users/user';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { changeTeamGroupLeaders } from '../../../../services/api/team/team';

interface IProps {
  handleClose: () => void;
  teamGroup: TeamGroup;
  disabled: boolean;
}

const emptyOptions: User[] = [];

const TeamGroupLeadersModal = ({
  teamGroup,
  handleClose,
  disabled,
}: IProps) => {
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly User[]>(emptyOptions);
  const [teamGroupLeaders, setTeamGroupLeaders] = useState<User[]>(
    teamGroup.leaders || []
  );

  const debouncedFetchUsers = useCallback(
    debounce(async (inputValue: string) => {
      setLoading(true);
      try {
        const response = await getUsers({
          page: 1,
          pageSize: 100,
          email: inputValue,
        });
        setOptions(response.results);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (inputValue === '') {
      setOptions(emptyOptions);
      return;
    }

    debouncedFetchUsers(inputValue);
  }, [inputValue, debouncedFetchUsers]);

  const handleRemoveLeader = (leaderId: string) => {
    setTeamGroupLeaders(
      teamGroupLeaders.filter((leader) => leader.id !== leaderId)
    );
  };

  const changeTeamGroupLeadersMutation = useMutation({
    mutationFn: (data: { groupId: string; leaders: string[] }) =>
      changeTeamGroupLeaders(data.groupId, data.leaders),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['TeamGroups'],
      });
      handleClose();
      toast.success('Capi gruppo cambiati con successo');
    },
    onError: () => {
      toast.error('Errore nel cambiare i capi gruppo');
    },
  });

  const handleSaveLeaders = () => {
    changeTeamGroupLeadersMutation.mutate({
      groupId: teamGroup.id,
      leaders: teamGroupLeaders.map((leader) => leader.id),
    });
  };

  return (
    <ModalContainer
      open
      handleClose={handleClose}
      title={`${teamGroup.name} - Capo gruppi`}
      containerSx={{ maxWidth: '500px' }}
      mtHeader={0}
    >
      <Box sx={{ p: 2, width: '100%' }}>
        <Autocomplete
          sx={{ width: '100%' }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.email}
          options={options}
          loading={loading}
          filterOptions={(x) => x}
          noOptionsText="Nessun utente"
          disabled={disabled}
          onChange={(event, newValue) => {
            if (
              newValue &&
              !teamGroupLeaders.some((leader) => leader.id === newValue.id)
            ) {
              setTeamGroupLeaders([...teamGroupLeaders, newValue]);
            }
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Aggiungi capo gruppo"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Box sx={{ mt: 2 }}>
          {teamGroupLeaders.map((leader) => (
            <Box key={leader.id} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography>{leader.email}</Typography>
              <IconButton onClick={() => handleRemoveLeader(leader.id)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mt: 4,
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <CtaButton
            variant="outlined"
            onClick={handleClose}
            disabled={changeTeamGroupLeadersMutation.isPending}
          >
            Annulla
          </CtaButton>
          <CtaButton
            variant="contained"
            onClick={handleSaveLeaders}
            loading={changeTeamGroupLeadersMutation.isPending}
            disabled={disabled}
          >
            Salva
          </CtaButton>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export default TeamGroupLeadersModal;
